import {useContext} from 'react'
import {useNavigate} from 'react-router-dom'
import {
    Box,
    Typography,
    Button,
    IconButton,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Paper, Container
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import CartContext from '../contexts/CartContext'
import {ICartItem} from '../models/ICart'
import {useTranslation} from "react-i18next";
import {SupportedLanguages} from "../models/Languages.ts";
import OrderContext from "../contexts/OrderContext.tsx";
import SaleContext from "../contexts/SaleContext.tsx";
import {getProductEffectivePrice} from "../functions/GetEffectivePrice.ts";

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const CartPage = () => {
    const {t, i18n} = useTranslation()
    const currentLanguage: SupportedLanguages = i18n.language as SupportedLanguages;
    const {selectedCurrency, rates} = useContext(OrderContext)
    const {sales} = useContext(SaleContext)
    const {removeCartItemsMutation, reserveCartItemsMutation, cart} = useContext(CartContext)
    const navigate = useNavigate()
    const userLocale = navigator.language || 'en-US';
  

    const handleRemoveItem = async (item: ICartItem, event: any) => {
        event.stopPropagation()
        removeCartItemsMutation.mutate(item)
    }

    const handleProductClick = (productId: string | undefined) => {
        navigate(`/product/${productId}`)
    }

    const handleCheckout = async () => {
        reserveCartItemsMutation.mutate()
        navigate('/checkout')
    }

    const calculateTotalPrice = (cartItems: any[]) => {
        if (!cartItems || cartItems.length === 0) {
            return 0;
        }
        return cartItems.reduce((total, item) => {
            if (!item || !item.product) {
                return total;
            }
            return total + getProductEffectivePrice(item.product, sales) * item.quantity * rates[selectedCurrency];
        }, 0);
    };

    if (cart) {
        return (
            <Container sx={{padding: '12px'}}>
                <Paper
                    elevation={4}

                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        maxWidth: '900px',
                        margin: 'auto',
                        padding: 2,
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        variant="h4"
                        gutterBottom
                        sx={{
                            marginBottom: 4,
                        }}
                    >
                        {t(`CART_PAGE_HEADER_TEXT`)}
                    </Typography>

                    {cart.items && cart.items.length === 0 ? (
                        <Typography
                            variant="body1"
                            sx={{
                                marginTop: 5,
                            }}
                        >
                            {t('CART_EMPTY_TEXT')}
                        </Typography>
                    ) : (
                        <Box sx={{width: '100%'}}>
                            <List sx={{padding: 0, margin: 0}}>
                                {cart.items &&
                                    cart.items.map((item: ICartItem, index: number) => (
                                        <Paper
                                            onClick={() => handleProductClick(item.product._id)}
                                            elevation={4}
                                            key={`${item.product._id}-${index}`}
                                            sx={{
                                                marginBottom: 2,
                                                padding: 1.5,
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                },
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <ListItem
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}>
                                                <ListItemAvatar>
                                                    <Avatar
                                                        src={`${VITE_API_BASE_URL}/${item.product.images[0]}`}
                                                        alt={item.product.name[currentLanguage]}
                                                        sx={{
                                                            width: '100px',
                                                            height: '100px',
                                                            borderRadius: 2,
                                                            marginRight: 3,
                                                        }}
                                                    />
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={item.product.name[currentLanguage]}
                                                    secondary={`${t('CART_PAGE_QUANTITY_TEXT')}: ${item.quantity} - ${new Intl.NumberFormat(userLocale, {
                                                        style: 'currency',
                                                        currency: selectedCurrency,
                                                    }).format(getProductEffectivePrice(item.product, sales) * rates[selectedCurrency])}`}
                                                    onClick={() => handleProductClick(item.product._id)}
                                                />
                                                <IconButton
                                                    onClick={(event) => handleRemoveItem(item, event)}
                                                    sx={{
                                                        color: 'red',
                                                        position: 'absolute',
                                                        bottom: 2,
                                                        right: 2,
                                                        transition: 'color 0.3s ease',
                                                        '&:hover': {
                                                            color: '#c0392b',
                                                        },
                                                    }}
                                                >
                                                    <DeleteIcon/>
                                                </IconButton>
                                            </ListItem>
                                        </Paper>
                                    ))}
                            </List>
                            <Typography variant="h5">
                                {t('CART_PAGE_SUM_TEXT')}: {new Intl.NumberFormat(userLocale, {
                                style: 'currency',
                                currency: selectedCurrency,
                            }).format(calculateTotalPrice(cart.items))}
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                sx={{
                                    padding: 2,
                                    fontSize: '18px',
                                    marginTop: 3,
                                }}
                                onClick={handleCheckout}
                            >
                                {t('CART_PAGE_PAY_BUTTON_TEXT')}
                            </Button>
                        </Box>
                    )}
                </Paper>
            </Container>
        )
    }
}

export default CartPage
